/**
 * PluginDeveloperDocs — in-app renderer for the plugin SDK author guides.
 *
 * Renders the markdown in docs/plugin-sdk/ directly (single source of truth,
 * no duplicated copy) so plugin developers can read the build/authoring docs
 * without leaving the app. Linked from the Plugin Store.
 */

import { useMemo } from "react";
import { Link } from "wouter";
import { ArrowLeft, BookOpen } from "lucide-react";
// Raw markdown guides — the same files shipped in the repo (docs/plugin-sdk/*).
import readmeMd from "../../docs/plugin-sdk/README.md?raw";
import developerGuideMd from "../../docs/plugin-sdk/PLUGIN-DEVELOPER-GUIDE.md?raw";
import platformFeaturesMd from "../../docs/plugin-sdk/PLUGIN-PLATFORM-FEATURES.md?raw";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { renderMarkdownToHtml } from "@/utils/markdownUtils";

const GUIDES: Array<{ value: string; label: string; markdown: string }> = [
  { value: "checklist", label: "Author Checklist", markdown: readmeMd },
  { value: "guide", label: "Developer Guide", markdown: developerGuideMd },
  { value: "features", label: "Platform Features", markdown: platformFeaturesMd },
];

function MarkdownPanel({ markdown }: { markdown: string }) {
  const html = useMemo(() => renderMarkdownToHtml(markdown), [markdown]);
  return (
    <ScrollArea className="h-[calc(100vh-16rem)] rounded-md border">
      <article
        className="prose prose-sm dark:prose-invert max-w-none p-6"
        // Content is authored in-repo and sanitized by renderMarkdownToHtml.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </ScrollArea>
  );
}

export default function PluginDeveloperDocs() {
  return (
    <div className="container mx-auto max-w-5xl p-4 md:p-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-6 w-6" /> Plugin Developer Docs
          </h1>
          <p className="text-muted-foreground mt-1">
            Everything you need to build, package, and publish a Deeply Smart plugin.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link href="/plugins">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Plugins
          </Link>
        </Button>
      </div>

      <Tabs defaultValue="checklist">
        <TabsList>
          {GUIDES.map((g) => (
            <TabsTrigger key={g.value} value={g.value}>
              {g.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {GUIDES.map((g) => (
          <TabsContent key={g.value} value={g.value} className="mt-4">
            <MarkdownPanel markdown={g.markdown} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
